import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import api from '../services/api';
import { WordPressProject, WordPressComment } from '../types';

const WordPressProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<WordPressProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<WordPressProject | null>(null);
  const [selectedComments, setSelectedComments] = useState<WordPressComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<any>(null);
  const [wardFilter, setWardFilter] = useState<string>('All');
  const [completionFilter, setCompletionFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<string>('comment');
  const [feedbackName, setFeedbackName] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string>('');
  const [feedbackSuccess, setFeedbackSuccess] = useState<string>('');
  const [feedbackImageUrl, setFeedbackImageUrl] = useState<string>('');
  const [feedbackImageUploading, setFeedbackImageUploading] = useState<boolean>(false);
  const [userResponseDialogOpen, setUserResponseDialogOpen] = useState(false);
  const [userResponseText, setUserResponseText] = useState('');
  const [activeFeedbackId, setActiveFeedbackId] = useState<string | null>(null);

  // Fetch all WordPress projects (we'll paginate client-side so filters work across all projects)
  const fetchProjects = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/wordpress-projects', {
        params: {
          limit: 1000,
          includeComments: true
        }
      });

      if (response.success) {
        setProjects(response.data || []);
      } else {
        setError(response.message || 'Failed to fetch WordPress projects');
      }
    } catch (err: any) {
      console.error('Error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch WordPress projects');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await api.get('/wordpress-projects/statistics');
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
      setStats({ totalProjects: 0, totalComments: 0, recentProjectsLastWeek: 0 });
    }
  };

  // Fetch comments for a specific project
  const fetchProjectComments = async (projectId: string) => {
    try {
      const response = await api.get(`/wordpress-projects/${projectId}/comments`);
      if (response.success) {
        setSelectedComments(response.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch comments:', err);
      setSelectedComments([]);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchStats();
  }, []);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSelectProject = (project: WordPressProject) => {
    setSelectedProject(project);
    fetchProjectComments(project.id);
  };

  const mapStatusToCompletion = (project: WordPressProject): number => {
    const explicitProgress =
      typeof project.progressPercentage === 'number'
        ? project.progressPercentage
        : Number(project.progressPercentage);

    if (!Number.isNaN(explicitProgress) && explicitProgress > 0) {
      return Math.max(0, Math.min(100, Math.round(explicitProgress)));
    }

    const statusText = (project.status || '').toLowerCase();

    // Parse statuses like "59% complete", "1%", "100% Complete".
    const percentageMatch = statusText.match(/(\d+(?:\.\d+)?)\s*%/);
    if (percentageMatch) {
      const parsed = parseFloat(percentageMatch[1]);
      if (!Number.isNaN(parsed)) {
        return Math.max(0, Math.min(100, Math.round(parsed)));
      }
    }

    if (
      statusText.includes('project not started') ||
      statusText.includes('not started') ||
      statusText.includes('has not taken off') ||
      statusText.includes('at inception') ||
      statusText.includes('awaiting') ||
      statusText.includes('nil')
    ) {
      return 0;
    }

    if (statusText.includes('complete')) {
      return 100;
    }

    if (statusText.includes('ongoing') || statusText.includes('progress')) {
      return 60;
    }

    if (statusText.includes('delay')) {
      return 40;
    }

    if (statusText.includes('planned')) {
      return 20;
    }

    return 0;
  };

  const matchesCompletionFilter = (percent: number): boolean => {
    switch (completionFilter) {
      case '0-10':
        return percent >= 0 && percent <= 10;
      case '11-20':
        return percent >= 11 && percent <= 20;
      case '21-30':
        return percent >= 21 && percent <= 30;
      case '31-40':
        return percent >= 31 && percent <= 40;
      case '41-50':
        return percent >= 41 && percent <= 50;
      case '51-60':
        return percent >= 51 && percent <= 60;
      case '61-70':
        return percent >= 61 && percent <= 70;
      case '71-80':
        return percent >= 71 && percent <= 80;
      case '81-90':
        return percent >= 81 && percent <= 90;
      case '91-100':
        return percent >= 91 && percent <= 100;
      default:
        return true;
    }
  };

  const matchesWardFilter = (project: WordPressProject): boolean => {
    if (wardFilter === 'All' || !project.ward) return true;
    const projectWard = project.ward.toLowerCase();
    const selectedWard = wardFilter.toLowerCase();
    return projectWard.includes(selectedWard);
  };

  const matchesStatusFilter = (project: WordPressProject): boolean => {
    if (statusFilter === 'all') return true;
    if (!project.status) return false;
    return project.status === statusFilter;
  };

  const filteredProjects = projects
    .filter(project => matchesWardFilter(project))
    .filter(project => {
      const percent = mapStatusToCompletion(project);
      return matchesCompletionFilter(percent);
    })
    .filter(project => matchesStatusFilter(project))
    .filter(project =>
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  // Client-side pagination so filters apply across all projects
  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedProjects = filteredProjects.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleOpenFeedbackDialog = () => {
    if (!selectedProject) return;
    setFeedbackType('comment');
    setFeedbackName('');
    setFeedbackMessage('');
    setFeedbackError('');
    setFeedbackSuccess('');
    setFeedbackImageUrl('');
    setFeedbackDialogOpen(true);
  };

  const handleCloseFeedbackDialog = () => {
    setFeedbackDialogOpen(false);
    setFeedbackImageUrl('');
  };

  const handleSubmitFeedback = async () => {
    if (!selectedProject) {
      setFeedbackError('Please select a project first.');
      return;
    }
    if (!feedbackMessage.trim()) {
      setFeedbackError('Please provide your feedback message.');
      return;
    }

    setSubmittingFeedback(true);
    setFeedbackError('');
    setFeedbackSuccess('');

    try {
      await api.submitFeedback({
        projectId: selectedProject.id,
        feedbackType,
        name: feedbackName.trim() || 'Anonymous',
        message: feedbackMessage.trim(),
        imageUrl: feedbackImageUrl || undefined,
      });

      setFeedbackSuccess('Thank you for your feedback.');
      setFeedbackDialogOpen(false);
      fetchProjectComments(selectedProject.id);
    } catch (err: any) {
      setFeedbackError(err.response?.data?.message || 'Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleOpenUserResponseDialog = (feedbackId: string) => {
    setActiveFeedbackId(feedbackId);
    setUserResponseText('');
    setUserResponseDialogOpen(true);
  };

  const handleCloseUserResponseDialog = () => {
    setUserResponseDialogOpen(false);
    setActiveFeedbackId(null);
    setUserResponseText('');
  };

  const handleSubmitUserResponse = async () => {
    if (!activeFeedbackId || !userResponseText.trim()) {
      return;
    }

    try {
      await api.post(`/feedback/${activeFeedbackId}/user-response`, {
        userResponse: userResponseText.trim(),
      });

      if (selectedProject) {
        await fetchProjectComments(selectedProject.id);
      }
      handleCloseUserResponseDialog();
    } catch (err: any) {
      console.error('Failed to submit user response', err);
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Box
        sx={{
          maxWidth: 'lg',
          mx: 'auto',
          mb: 4,
          px: 2,
        }}
      >
        <Box
          sx={{
            borderRadius: 3,
            px: { xs: 3, md: 6 },
            py: { xs: 4, md: 6 },
            background: 'linear-gradient(120deg, #003c8f 0%, #0059c0 60%, #0073e6 100%)',
            color: '#fff',
            textAlign: { xs: 'left', md: 'left' },
            boxShadow: '0 18px 45px rgba(0, 60, 143, 0.35)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Project Monitoring and Tracking System
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.9 }}>
            Projects Overview
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ pb: 4 }}>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="ward-filter-label">Ward</InputLabel>
            <Select
              labelId="ward-filter-label"
              value={wardFilter}
              label="Ward"
              onChange={(e) => setWardFilter(e.target.value)}
            >
              <MenuItem value="All">All Wards</MenuItem>
              <MenuItem value="Port Reitz">Port Reitz</MenuItem>
              <MenuItem value="Kipevu">Kipevu</MenuItem>
              <MenuItem value="Airport">Airport</MenuItem>
              <MenuItem value="Changamwe">Changamwe</MenuItem>
              <MenuItem value="Chaani">Chaani</MenuItem>
              <MenuItem value="Jomvu Kuu">Jomvu Kuu</MenuItem>
              <MenuItem value="Miritini">Miritini</MenuItem>
              <MenuItem value="Mikindani.">Mikindani.</MenuItem>
              <MenuItem value="Mjambere">Mjambere</MenuItem>
              <MenuItem value="Junda">Junda</MenuItem>
              <MenuItem value="Bamburi">Bamburi</MenuItem>
              <MenuItem value="Mwakirunge">Mwakirunge</MenuItem>
              <MenuItem value="Mtopanga">Mtopanga</MenuItem>
              <MenuItem value="Magogoni">Magogoni</MenuItem>
              <MenuItem value="Shanzu">Shanzu</MenuItem>
              <MenuItem value="Frere Town">Frere Town</MenuItem>
              <MenuItem value="Ziwa la Ng'ombe">Ziwa la Ng'ombe</MenuItem>
              <MenuItem value="Mkomani">Mkomani</MenuItem>
              <MenuItem value="Kongowea">Kongowea</MenuItem>
              <MenuItem value="Kadzandani">Kadzandani</MenuItem>
              <MenuItem value="Mtongwe">Mtongwe</MenuItem>
              <MenuItem value="Shika Adabu">Shika Adabu</MenuItem>
              <MenuItem value="Bofu">Bofu</MenuItem>
              <MenuItem value="Likoni">Likoni</MenuItem>
              <MenuItem value="Timbwani">Timbwani</MenuItem>
              <MenuItem value="Tudor">Tudor</MenuItem>
              <MenuItem value="Mji wa Kale/Makadara">Mji wa Kale/Makadara</MenuItem>
              <MenuItem value="Tononoka">Tononoka</MenuItem>
              <MenuItem value="Shimanzi/Ganjoni">Shimanzi/Ganjoni</MenuItem>
              <MenuItem value="Majengo">Majengo</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="completion-filter-label">Completion</InputLabel>
            <Select
              labelId="completion-filter-label"
              value={completionFilter}
              label="Completion"
              onChange={(e) => setCompletionFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="0-10">0% - 10%</MenuItem>
              <MenuItem value="11-20">11% - 20%</MenuItem>
              <MenuItem value="21-30">21% - 30%</MenuItem>
              <MenuItem value="31-40">31% - 40%</MenuItem>
              <MenuItem value="41-50">41% - 50%</MenuItem>
              <MenuItem value="51-60">51% - 60%</MenuItem>
              <MenuItem value="61-70">61% - 70%</MenuItem>
              <MenuItem value="71-80">71% - 80%</MenuItem>
              <MenuItem value="81-90">81% - 90%</MenuItem>
              <MenuItem value="91-100">91% - 100%</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="planned">Planned</MenuItem>
              <MenuItem value="ongoing">Ongoing</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="delayed">Delayed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Statistics */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h5" color="primary">
                {stats.totalProjects}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Projects
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h5" color="primary">
                {stats.totalComments}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Comments
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h5" color="success.main">
                {stats.recentProjectsLastWeek}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Projects (Last Week)
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search projects..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      <Grid container spacing={3}>
        {/* Projects List */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                {wardFilter !== 'All'
                  ? `${wardFilter} Project Implementation Status`
                  : 'Project Implementation Status'}
              </Typography>

              {loading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress />
                </Box>
              ) : filteredProjects.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                  No projects found
                </Typography>
              ) : (
                <Box sx={{ maxHeight: '700px', overflow: 'auto' }}>
                  {pagedProjects.map(project => {
                    const percent = mapStatusToCompletion(project);
                    return (
                      <Paper
                        key={project.id}
                        sx={{
                          p: 2,
                          mb: 2,
                          cursor: 'pointer',
                          backgroundColor: selectedProject?.id === project.id ? '#fff8e1' : '#ffffff',
                          '&:hover': { backgroundColor: '#f9fafb' },
                          borderRadius: 3,
                          boxShadow: '0 10px 24px rgba(15,23,42,0.08)',
                        }}
                        onClick={() => handleSelectProject(project)}
                      >
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={6}>
                            <Box
                              sx={{
                                bgcolor: '#111827',
                                borderRadius: 999,
                                overflow: 'hidden',
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Box
                                sx={{
                                  bgcolor: '#ffb300',
                                  height: '100%',
                                  width: `${percent}%`,
                                  minWidth: percent > 0 ? '15%' : '0%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  px: 2,
                                  color: '#ffffff',
                                  fontWeight: 500,
                                  borderRadius: 999,
                                }}
                              >
                                <Typography variant="body2">Complete</Typography>
                                <Typography variant="body2">{`${percent}%`}</Typography>
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600, mb: 0.5, color: '#111827' }}
                            >
                              {project.title}
                            </Typography>
                            {project.ward && (
                              <Typography variant="caption" color="textSecondary">
                                {project.ward} Ward
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  })}
                </Box>
              )}

              {totalPages > 1 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Project Details & Comments */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              {selectedProject ? (
                <Box>
                  {/* Project Details */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                        {selectedProject.title}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                      {selectedProject.wpUrl && (
                        <Button
                          variant="outlined"
                          size="small"
                          endIcon={<OpenInNewIcon />}
                          onClick={() => window.open(selectedProject.wpUrl, '_blank')}
                        >
                          View on WordPress
                        </Button>
                      )}
                      {selectedProject.serialNo && (
                        <Chip label={`Project Serial No: ${selectedProject.serialNo}`} size="small" />
                      )}
                    </Box>

                    {selectedProject.featuredImage && (
                      <CardMedia
                        component="img"
                        height="250"
                        image={selectedProject.featuredImage}
                        alt={selectedProject.title}
                        sx={{ mb: 2, borderRadius: 1 }}
                      />
                    )}

                    {selectedProject.description && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Description
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', whiteSpace: 'pre-wrap' }}>
                          {selectedProject.description}
                        </Typography>
                      </Box>
                    )}

                    {selectedProject.content && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Content
                        </Typography>
                        <Box
                          sx={{
                            p: 1.5,
                            backgroundColor: '#f5f5f5',
                            borderRadius: 1,
                            maxHeight: '200px',
                            overflow: 'auto'
                          }}
                          dangerouslySetInnerHTML={{ __html: selectedProject.content }}
                        />
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Comments Section */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                        Comments ({selectedComments.length})
                      </Typography>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={handleOpenFeedbackDialog}
                      >
                        Add Feedback
                      </Button>
                    </Box>

                    {selectedComments.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        No comments yet
                      </Typography>
                    ) : (
                      <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
                        {selectedComments.map(comment => (
                          <Box key={comment.id}>
                            <ListItem alignItems="flex-start" sx={{ px: 0, py: 2 }}>
                              <Avatar
                                sx={{
                                  mr: 2,
                                  backgroundColor: '#1976d2',
                                  width: 40,
                                  height: 40
                                }}
                              >
                                {comment.name?.[0]?.toUpperCase() || 'A'}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                    {comment.name || 'Anonymous'}
                                  </Typography>
                                  <Chip
                                    label={comment.status}
                                    size="small"
                                    variant="outlined"
                                    color={comment.status === 'resolved' ? 'success' : 'default'}
                                  />
                                </Box>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                                  {new Date(comment.createdAt).toLocaleString()}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#333', whiteSpace: 'pre-wrap', mb: 1 }}>
                                  {comment.message}
                                </Typography>
                                {comment.imageUrl && (
                                  <Button
                                    size="small"
                                    onClick={() => window.open(comment.imageUrl!, '_blank')}
                                    sx={{ mb: 1 }}
                                  >
                                    View Image
                                  </Button>
                                )}
                                {comment.response && (
                                  <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, bgcolor: '#f3f4f6' }}>
                                    <Typography variant="caption" color="textSecondary">
                                      Admin response:
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {comment.response}
                                    </Typography>
                                  </Box>
                                )}
                                {comment.response && (
                                  <Box sx={{ mt: 1 }}>
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => handleOpenUserResponseDialog(comment.id)}
                                    >
                                      Respond to admin
                                    </Button>
                                  </Box>
                                )}
                                {comment.userResponse && (
                                  <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, bgcolor: '#eef2ff' }}>
                                    <Typography variant="caption" color="textSecondary">
                                      Your response:
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {comment.userResponse}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </ListItem>
                            <Divider />
                          </Box>
                        ))}
                      </List>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
                  <Typography variant="body1" color="textSecondary">
                    Select a project to view details and comments
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>

    {/* Feedback Dialog */}
    <Dialog open={feedbackDialogOpen} onClose={handleCloseFeedbackDialog} maxWidth="sm" fullWidth>
      <DialogTitle>Add Feedback</DialogTitle>
      <DialogContent>
        {feedbackError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {feedbackError}
          </Alert>
        )}
        {feedbackSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {feedbackSuccess}
          </Alert>
        )}
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="feedback-type-label">Feedback Type</InputLabel>
            <Select
              labelId="feedback-type-label"
              value={feedbackType}
              label="Feedback Type"
              onChange={(e) => setFeedbackType(e.target.value)}
            >
              <MenuItem value="comment">Comment</MenuItem>
              <MenuItem value="suggestion">Suggestion</MenuItem>
              <MenuItem value="complaint">Complaint</MenuItem>
              <MenuItem value="appreciation">Appreciation</MenuItem>
              <MenuItem value="query">Query</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Your Name"
            fullWidth
            size="small"
            value={feedbackName}
            onChange={(e) => setFeedbackName(e.target.value)}
          />
          <TextField
            label="Your Feedback"
            fullWidth
            multiline
            minRows={4}
            value={feedbackMessage}
            onChange={(e) => setFeedbackMessage(e.target.value)}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              component="label"
              disabled={feedbackImageUploading}
            >
              {feedbackImageUploading ? 'Uploading Image...' : 'Attach Image'}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={async (e: any) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setFeedbackError('');
                  setFeedbackImageUploading(true);
                  try {
                    const res = await api.uploadFile(file);
                    setFeedbackImageUrl(res.data.fileUrl);
                  } catch (err: any) {
                    setFeedbackError(err.response?.data?.message || 'Failed to upload image');
                  } finally {
                    setFeedbackImageUploading(false);
                  }
                }}
              />
            </Button>
            {feedbackImageUrl && (
              <Typography variant="caption" color="textSecondary">
                Image attached
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseFeedbackDialog} disabled={submittingFeedback}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmitFeedback}
          variant="contained"
          disabled={submittingFeedback}
        >
          {submittingFeedback ? <CircularProgress size={20} /> : 'Submit Feedback'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* User Response Dialog */}
    <Dialog open={userResponseDialogOpen} onClose={handleCloseUserResponseDialog} maxWidth="sm" fullWidth>
      <DialogTitle>Respond to Admin</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Your Response"
          value={userResponseText}
          onChange={(e) => setUserResponseText(e.target.value)}
          placeholder="Enter your response to the admin's message"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseUserResponseDialog}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmitUserResponse} disabled={!userResponseText.trim()}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  </Box>
  );
};

export default WordPressProjectsPage;
